import { action, computed, observable } from 'mobx';
import last from 'lodash/last';
import sumBy from 'lodash/sumBy';
import { CXContainerableBlock } from '~/components/ClientExperience/OneFlow/models/cx-containerable-block.model';
import type { CXPaymentBlock } from '~/components/ClientExperience/OneFlow/models/cx-payment-block.model';
import {
    IRecurringPayment,
    IRecurringSchedule,
    PaymentStatus
} from '~/types/model.types';
import { IRecurringScheduleBlockResponse } from '~/types/server.types';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import type { FlowRevision } from '~/models/flow-revision.model';

export class CXRecurringScheduleBlock extends CXContainerableBlock {
    type: 'recurring_schedule_block';
    @observable recurring_payments: boolean;
    @observable sent_invoices_count: number;
    @observable upcoming_invoices_count: number;
    @observable paid_payments_count: number;
    @observable recurring_schedule: IRecurringSchedule;
    @observable upcoming_invoices: IRecurringPayment[];
    @observable current_payment_block_id: string;

    constructor(
        block: IRecurringScheduleBlockResponse,
        flowRevision: FlowRevision<CXRootStore>,
        rootStore: CXRootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    get isRecurringInvoice() {
        return true;
    }

    @computed
    get currentPaymentBlock() {
        return !!this.current_payment_block_id
            ? this.paymentBlockById(this.current_payment_block_id)
            : (last(this.childBlocks) as CXPaymentBlock);
    }

    @computed
    get paymentBlocks() {
        return this.childBlocks as CXPaymentBlock[];
    }

    @computed
    get totalAmount() {
        return sumBy(this.childBlocks, (payment: CXPaymentBlock) => {
            return payment.paymentAmount;
        });
    }

    @computed
    get shouldDisplayPaymentDateColumn() {
        return this.childBlocks.some((payment: CXPaymentBlock) => {
            return payment.paymentChargeDate;
        });
    }

    private paymentBlockById(paymentBlockId: string) {
        return (this.childBlocks as CXPaymentBlock[])?.find(
            child => child.block_id === paymentBlockId
        ) as CXPaymentBlock;
    }

    @computed
    get lastPaymentBlock() {
        return last(this.childBlocks) as CXPaymentBlock;
    }

    @computed
    get isLastRecurringPaymentOverdue() {
        return this.lastPaymentBlock.paymentStatus === PaymentStatus.Overdue;
    }

    @computed
    get upcomingPaymentBlock() {
        return this.flowRevision.upcomingPayment;
    }

    @computed
    get upcomingPaymentBlockNonOverdue() {
        return this.flowRevision.upcomingPaymentNonOverdue;
    }

    @computed
    get upcomingInvoices() {
        return this.upcoming_invoices;
    }

    @computed
    get nextPaymentBlock(): CXPaymentBlock {
        const nextPaymentBlock = this.childBlocks.find(
            (payment: CXPaymentBlock) => {
                return !payment.isPaid && !payment.isRefunded;
            }
        );
        return nextPaymentBlock as CXPaymentBlock;
    }

    @computed
    get hasPaidPayments() {
        return this.paidPayments.length > 0;
    }

    @computed
    get paidPayments() {
        return this.childBlocks.filter((payment: CXPaymentBlock) => {
            return payment.isPaid || payment.isRefunded;
        });
    }

    @computed
    get isActive() {
        return this.connectedVariable?.value?.is_active;
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);

        if (block.paid_payments_count) {
            this.paid_payments_count = block.paid_payments_count;
        }
    }
}
